import SyncIcon from "@mui/icons-material/Sync";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { NisParticipantResponse } from "../../../../api/services/nisParticipants/models";
import { useGetNISParticipantStatusById } from "../../../../api/services/nisParticipants/nisParticipantsApis";
import {
  MuiBox,
  MuiTypography,
  STATUS_BADGE_TYPE,
  StatusBadge,
  StatusBadgeType,
} from "../../../../components";
import { ButtonWithIcon } from "../../../../components/buttonWithIcon/ButtonWithIcon";
import { PAGE_IDS } from "../../../../helpers/constants";
import { getValueOrBlank, isNotEmpty } from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { createNISParticipantNISStatusSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { NISStatusWrapper } from "./NISStatus.style";
import { ChangeNISStatus } from "./components/changeNISStatus/ChangeNISStatus";
import { isStatusCreatedInError } from "./components/changeNISStatus/helper";
import { NISStatusResult } from "./components/nisStatusResult/NISStatusResult";

const pageId = PAGE_IDS.CREATE_NIS_PARTICIPANT_NIS_STATUS;

interface NISStatusProps {
  isEdit?: boolean;
  isViewOnly?: boolean;
  nisParticipantId?: string;
  editNISParticipantData?: NisParticipantResponse;
}

export const NISStatus: FC<NISStatusProps> = ({
  isEdit = false,
  isViewOnly = false,
  nisParticipantId,
  editNISParticipantData,
}) => {
  const { commonLabels, useFetchLabel, fetchValueByKeyOfDropdown } =
    useSiteCore();
  const { displayToast } = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {
    nisStatusTitle,
    currentStatus,
    changeStatusPopupHeader,
    nisParticipantStatusList,
  } = createNISParticipantNISStatusSiteCoreKeys;
  const getLabel = useFetchLabel(nisStatusTitle);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);

  const {
    data: nisStatusListData,
    mutateAsync: refetchNISStatusList,
    isPending: isNISStatusListLoading,
    isError: isNISStatusListError,
  } = useGetNISParticipantStatusById();

  useEffect(() => {
    if ((isEdit || isViewOnly) && isNotEmpty(nisParticipantId)) {
      refetchNISStatusList(nisParticipantId);
    }
  }, [isEdit, isViewOnly, nisParticipantId]);

  useEffect(() => {
    if (isNISStatusListError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isNISStatusListError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isNISStatusFetching: {
          isVisible: isNISStatusListLoading,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isNISStatusFetching: { isVisible: false } }));
    };
  }, [isNISStatusListLoading]);

  const currentNISStatusId = useMemo(() => {
    return (
      (((isEdit || isViewOnly) &&
        editNISParticipantData?.profile?.statusId) as StatusBadgeType) ||
      STATUS_BADGE_TYPE.open
    );
  }, [isEdit, isViewOnly, editNISParticipantData]);

  const handleCloseChangeStatusModal = () => {
    setShowChangeStatusModal(false);
  };

  const handleNISStatusUpdate = () => {
    handleCloseChangeStatusModal();
    if (isNotEmpty(nisParticipantId)) {
      refetchNISStatusList(nisParticipantId);
      // Invalidate participant data query to trigger automatic refetch
      queryClient.invalidateQueries({
        queryKey: ["nisParticipant", nisParticipantId],
      });
      // Invalidate activity timeline query to trigger automatic refetch
      queryClient.invalidateQueries({
        queryKey: ["useGetNISParticipantActivitiesList", nisParticipantId],
      });
    }
  };

  return (
    <NISStatusWrapper>
      <MuiBox className="status-label">
        <MuiTypography variant="h3">{getLabel(currentStatus)}</MuiTypography>
        <MuiBox className="active-change-status">
          <StatusBadge
            status={currentNISStatusId}
            label={fetchValueByKeyOfDropdown(
              nisStatusTitle,
              nisParticipantStatusList,
              currentNISStatusId,
            )}
          />
          {isEdit && !isStatusCreatedInError(currentNISStatusId) && (
            <ButtonWithIcon
              buttonText={getLabel(changeStatusPopupHeader)}
              ActionIcon={SyncIcon}
              data-testid={`${pageId}-change-status`}
              onActionClick={() => setShowChangeStatusModal(true)}
            />
          )}
        </MuiBox>
      </MuiBox>

      {(isEdit || isViewOnly) && (
        <NISStatusResult
          rows={nisStatusListData ?? []}
          isEditNISParticipant={isEdit}
        />
      )}

      {showChangeStatusModal && (
        <ChangeNISStatus
          nisParticipantId={getValueOrBlank(nisParticipantId)}
          open={showChangeStatusModal}
          onClose={handleCloseChangeStatusModal}
          currentNISStatusId={currentNISStatusId}
          onNISStatusUpdated={handleNISStatusUpdate}
        />
      )}
    </NISStatusWrapper>
  );
};
